// job-web-main/frontend/src/components/dashboard/StatusTab.js
import { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, ChevronDown } from 'lucide-react';
import { getDashboardStatus, getVisitInfo } from '../../api/portal';
import VisitBookingWidget from './VisitBookingWidget';
import './dashboardTabs.css';

const CANDIDATE_STAGES = ['registered', 'documents_verified', 'office_visit', 'shortlisted', 'interview', 'result', 'offer', 'visa', 'deployed'];
const STAGE_LABELS = { registered: 'Registered', documents_verified: 'Documents verified', office_visit: 'Office visit', shortlisted: 'Shortlisted', interview: 'Interview', result: 'Result', offer: 'Offer & documents', visa: 'Visa & travel', deployed: 'Deployed' };

const STATE_COPY = {
  review: { eyebrow: 'Status', title: "We're reviewing your documents", body: 'This usually takes 1–2 working days.' },
  application_rejected: { eyebrow: 'Action needed', title: 'We need another look at your documents', body: null },
  // documents_verified stateId removed (code-review, 2026-07-25) — that population now resolves to
  // book-visit/visit-confirmed/visit-overdue instead (isPreVisitStage on the backend covers both the
  // Registration and AwaitingVisit internal stages); `documents_verified` survives only as a
  // CANDIDATE_STAGES progress-bar label, never as a stateId anymore.
  office_visit: { eyebrow: 'Status', title: 'Office visit', body: "We'll be in touch to schedule your office visit." },
  'book-visit': { eyebrow: 'Action needed', title: 'Come in to go through your matches', body: 'Your documents are verified. Visit our office so we can go through the roles matching you — bring your original passport.' },
  // rnr (bug #6): staff tried to call and couldn’t reach the candidate. Warm, blame-free, and paired
  // with the booking widget below (rnr is in VISIT_STATES) so "update your availability" is actionable.
  rnr: { eyebrow: 'We tried to reach you', title: 'We couldn’t get through', body: 'Our team tried to call you about your application but couldn’t reach you. Please expect our call — or pick a time to come in below so we can connect.' },
  'visit-confirmed': { eyebrow: "You're booked", title: null, body: null },
  'visit-overdue': { eyebrow: 'Action needed', title: 'Your visit is overdue', body: 'We didn’t see you at the office for your booked visit. Let’s find a new time.' },
  no_match: { eyebrow: 'Status', title: "We don't have a role matching you right now", body: "We'll get back to you as soon as a suitable opening comes up. Check your Matches tab any time." },
  all_declined: { eyebrow: 'Status', title: "You've declined all your matches", body: "We'll let you know the moment a new one comes up." },
  shortlisted: { eyebrow: 'Great news', title: null, body: 'You agreed to this role and you’re a match. We’ll call you soon to arrange your interview.' },
  interview_scheduled: { eyebrow: 'Interview', title: 'Your interview is scheduled', body: "We'll be in touch with the details — keep an eye on your phone." },
  interview_missed: { eyebrow: 'Status', title: "We'll try you again", body: 'We couldn’t reach you for your interview. Please make sure to attend without fail once we confirm a new time.' },
  result_pending: { eyebrow: 'Status', title: 'Awaiting result', body: "We'll let you know as soon as we hear back." },
  not_selected: { eyebrow: 'Status', title: 'Not selected this time', body: "We'll keep you in mind for other roles that match you." },
  selected: { eyebrow: '', title: '', body: '' }, // celebration render path, not the plain card
  offer: { eyebrow: 'Action needed', title: 'Your role is confirmed', body: 'Come in to sign your offer letter.' },
  visa: { eyebrow: 'Visa issued', title: 'Please visit our office', body: 'Pick a time to come in, or call us if you have questions.' },
  deployed: { eyebrow: 'Welcome', title: "You're deployed", body: 'We hope the new role is going well.' },
};

const VISIT_STATES = new Set(['book-visit', 'rnr', 'visit-confirmed', 'visit-overdue']);

export default function StatusTab() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [journeyOpen, setJourneyOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [visitInfo, setVisitInfo] = useState(null);

  function loadStatus() {
    return getDashboardStatus();
  }

  useEffect(() => {
    let live = true;
    loadStatus().then((s) => { if (live) setStatus(s); }).catch(() => { if (live) setError('Could not load your status. Please try again.'); });
    getVisitInfo().then((v) => { if (live) setVisitInfo(v); }).catch(() => {}); // non-fatal — widget just won't show a phone number
    return () => { live = false; };
  }, []);

  if (error) return <div className="apply-error" role="alert">{error}</div>;
  if (!status) return null;

  const copy = STATE_COPY[status.stateId] || STATE_COPY.review;
  let title = status.stateId === 'shortlisted' && status.demandTitle ? `Shortlisted for ${status.demandTitle}` : copy.title;
  let body = copy.body;
  if (status.stateId === 'visit-confirmed' && status.officeVisitDate) {
    const d = new Date(status.officeVisitDate);
    title = d.toLocaleDateString('en-GB', { weekday: 'long' }) + ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  if (status.stateId === 'visit-overdue' && status.officeVisitDate) {
    body = `You booked a visit for ${new Date(status.officeVisitDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} but we didn’t see you at the office. Let’s find a new time — or call us if something came up.`;
  }

  return (
    <div className="dash-panel">
      {status.docIssue && (
        <div className="dtab-alert">
          <AlertTriangle size={18} className="dtab-alert-icon" />
          <div>
            <p className="dtab-alert-title">{status.docIssue.documentName} needs attention</p>
            <p className="dtab-alert-body">{status.docIssue.reason}</p>
          </div>
        </div>
      )}

      {status.stateId === 'selected' ? (
        <Celebration demandTitle={status.demandTitle} />
      ) : (
        <>
          {status.stageIndex != null && (
            <div className="dtab-progress-row">
              <div className="dtab-progress-track">
                <div className="dtab-progress-fill" style={{ transform: `scaleX(${(status.stageIndex + 1) / status.totalStages})` }} />
              </div>
              <span className="dtab-progress-label">Step {status.stageIndex + 1} of {status.totalStages} · {STAGE_LABELS[status.stage]}</span>
            </div>
          )}
          <div className="dtab-na">
            <p className="dtab-na-eyebrow">{copy.eyebrow}</p>
            {title && <h3 className="dtab-na-title">{title}</h3>}
            {body && <p className="dtab-na-body">{body}</p>}
            {VISIT_STATES.has(status.stateId) && (
              <div style={{ marginTop: 12 }}>
                <VisitBookingWidget
                  mode={status.stateId === 'visit-confirmed' ? 'confirmed' : 'booking'}
                  officeVisitDate={status.officeVisitDate}
                  address={visitInfo?.address}
                  phone={visitInfo?.phone}
                  onBooked={() => { loadStatus().then(setStatus).catch(() => {}); }}
                />
              </div>
            )}
          </div>
        </>
      )}

      {status.interviewHistory && status.interviewHistory.total > 0 && (
        <div className="dash-panel" style={{ marginTop: 16 }}>
          <p className="dtab-lv-label">Interview history</p>
          <p className="dtab-lv-value" style={{ fontSize: 14 }}>
            {status.interviewHistory.missed > 0
              ? `You've missed ${status.interviewHistory.missed} of ${status.interviewHistory.total} interviews so far`
              : `${status.interviewHistory.total} interview${status.interviewHistory.total > 1 ? 's' : ''} so far`}
          </p>
          <button type="button" className="dtab-journey-toggle" onClick={() => setHistoryOpen((v) => !v)}>
            <span className="dtab-journey-toggle-label">{historyOpen ? 'Hide' : 'View'} details</span>
            <ChevronDown size={16} className={historyOpen ? 'dtab-journey-chevron is-open' : 'dtab-journey-chevron'} />
          </button>
          {historyOpen && (
            <ul className="dtab-hist-list">
              {status.interviewHistory.rounds.map((r, i) => (
                <li key={i} className="dtab-hist-row">
                  <span>{r.date ? new Date(r.date).toLocaleDateString() : 'Not yet scheduled'}</span>
                  <span className={r.outcome === 'Missed' ? 'dtab-hist-outcome is-missed' : 'dtab-hist-outcome'}>{r.outcome}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {status.stageIndex != null && (
        <button type="button" className="dtab-journey-toggle" onClick={() => setJourneyOpen((v) => !v)}>
          <span className="dtab-journey-toggle-label">{journeyOpen ? 'Hide' : 'View'} full journey</span>
          <ChevronDown size={16} className={journeyOpen ? 'dtab-journey-chevron is-open' : 'dtab-journey-chevron'} />
        </button>
      )}
      {journeyOpen && status.stageIndex != null && (
        <ul className="dtab-tracker">
          {CANDIDATE_STAGES.map((s, i) => (
            <li key={s} className={i < status.stageIndex ? 'dtab-step is-done' : i === status.stageIndex ? 'dtab-step is-active' : 'dtab-step'}>
              <span className="dtab-step-dot">{i < status.stageIndex ? <CheckCircle2 size={11} /> : null}</span>
              <p className="dtab-step-label">{STAGE_LABELS[s]}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Celebration({ demandTitle }) {
  return (
    <div className="dtab-celebrate">
      <div className="dtab-celebrate-bloom" />
      <svg className="dtab-celebrate-check" viewBox="0 0 56 56" fill="none">
        <circle cx="28" cy="28" r="26" stroke="currentColor" strokeWidth="3" />
        <path d="M17 29l7 7 15-15" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <p className="dtab-celebrate-eyebrow">Selected</p>
      <h3 className="dtab-celebrate-title">{demandTitle ? `You're selected for ${demandTitle}` : "You're selected!"}</h3>
      <p className="dtab-celebrate-sub">We'll be in touch about your offer letter shortly.</p>
    </div>
  );
}
